import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, User, Lock } from "lucide-react";
import { toast } from "sonner";

const BIHAR_LOGO = "https://customer-assets-gfyr7b9c.emergentagent.net/job_workforce-dashboard-73/artifacts/vbcjdbyb_image.png";

function genCaptcha() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 5 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState(genCaptcha());
  const [captchaInput, setCaptchaInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const captchaStyle = useMemo(() => ({}), [captcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (captchaInput.trim().toUpperCase() !== captcha) {
      setError("Captcha does not match. Please try again.");
      setCaptcha(genCaptcha());
      setCaptchaInput("");
      return;
    }
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome, ${u.name}`);
      navigate("/");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      setCaptcha(genCaptcha());
      setCaptchaInput("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3ede4] flex flex-col md:flex-row" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden md:flex md:w-1/2 lg:w-3/5 relative bg-primary text-primary-foreground flex-col justify-between p-12 gov-band overflow-hidden">
        <div className="relative z-10 flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-white border-2 border-primary-foreground/30 flex items-center justify-center overflow-hidden p-1.5">
            <img src={BIHAR_LOGO} alt="Government of Bihar" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-heading text-4xl font-extrabold tracking-tight leading-none">HRMS</div>
            <div className="text-sm tracking-[0.2em] uppercase text-primary-foreground/80 mt-1">Government of Bihar</div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-tight">Human Resource Management System</h1>
          <p className="mt-5 text-primary-foreground/85 text-lg leading-relaxed">
            A unified platform for employee records, attendance, leave management and departmental administration across the state.
          </p>
        </div>
        <div className="relative z-10 text-sm text-primary-foreground/70">© {new Date().getFullYear()} Government of Bihar. All rights reserved.</div>
        <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-accent/20 blur-2xl" />
        <div className="absolute right-10 top-10 w-40 h-40 rounded-full border border-primary-foreground/10" />
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Mobile header */}
          <div className="md:hidden flex items-center gap-3 mb-6 text-primary">
            <div className="w-12 h-12 rounded-full bg-white border border-border flex items-center justify-center overflow-hidden p-1">
              <img src={BIHAR_LOGO} alt="Government of Bihar" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-heading text-3xl font-extrabold leading-none">HRMS</div>
              <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Government of Bihar</div>
            </div>
          </div>

          <div className="bg-card rounded-xl shadow-xl border border-border overflow-hidden">
            <div className="bg-primary text-primary-foreground px-8 py-6 text-center">
              <h2 className="font-heading text-3xl font-bold">Sign in</h2>
              <p className="text-primary-foreground/80 text-sm mt-1">Access the HRMS using your credentials</p>
            </div>

            <div className="p-8">
              {/* Tabs */}
              <div className="flex mb-6 border-b border-border">
                <button
                  type="button"
                  data-testid="tab-by-password"
                  onClick={() => setTab("password")}
                  className={`px-5 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors ${tab === "password" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                >
                  By Password
                </button>
                <button
                  type="button"
                  data-testid="tab-by-otp"
                  onClick={() => { setTab("otp"); toast.info("OTP login is not available in this demo. Use password."); }}
                  className={`px-5 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors ${tab === "otp" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                >
                  By OTP
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="text-sm font-semibold text-foreground">User Name</label>
                  <div className="relative mt-1.5">
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      data-testid="login-email-input"
                      type="text"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9 bg-secondary/60"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-foreground">Password</label>
                  <div className="relative mt-1.5">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      data-testid="login-password-input"
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-9 bg-secondary/60"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-foreground">Captcha</label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div
                      className="select-none font-mono text-xl font-bold tracking-[0.35em] px-4 py-2 rounded-md bg-secondary border border-border text-primary italic"
                      style={{ textDecoration: "line-through", textDecorationColor: "rgba(0,0,0,0.15)" }}
                      data-testid="captcha-code"
                    >
                      {captcha}
                    </div>
                    <Input
                      data-testid="captcha-input"
                      type="text"
                      placeholder="Enter code"
                      value={captchaInput}
                      onChange={(e) => setCaptchaInput(e.target.value)}
                      className="flex-1 bg-secondary/60"
                      required
                    />
                    <button
                      type="button"
                      data-testid="captcha-refresh"
                      onClick={() => { setCaptcha(genCaptcha()); setCaptchaInput(""); }}
                      className="p-2.5 rounded-md bg-accent text-accent-foreground hover:opacity-90 transition-opacity"
                      title="Refresh captcha"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {error && (
                  <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                    {error}
                  </div>
                )}

                <Button
                  data-testid="login-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold text-base py-5"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Login"}
                </Button>

                <div className="text-center">
                  <button type="button" className="text-sm font-semibold text-primary hover:underline" onClick={() => toast.info("Please contact your HR administrator to reset your password.")}>
                    Forgot username / password?
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
